import type {
    Module,
    Question,
} from "components/Admin/Organizations/types/Module.type";
import type { SlimLLMClassifierResponse } from "models/LLMs/LLMClassifier";
import type { ISignalsSelectionTypes } from "../../../SignalsModels";

export type ChartSelectionField = { label: string; values: string[] };

type ChartSelectionInput = {
    selectionType: ISignalsSelectionTypes;
    selectedIds: Partial<Record<string, string | string[] | undefined>>;
    /** The modules the chart's module dropdown offers. */
    modules: Module[];
    llmClassifiers: Pick<SlimLLMClassifierResponse, "id" | "name">[];
    /** Charts with a data grid pick questions, answers and modules in the grid, not in a dropdown. */
    hasDataGrid: boolean;
    /** The selected skill grouping's option label, for charts with a skill grouping selector. */
    skillGroupingLabel?: string;
};

const toIds = (value?: string | string[]): string[] =>
    Array.isArray(value)
        ? value.map((id) => `${id}`)
        : value
        ? [`${value}`]
        : [];

// Signals maps questions with a `displayName`, which is what the question dropdowns show.
const questionName = (question: Question & { displayName?: string }) =>
    question.displayName ?? question.shortQuestionText ?? question.questionText;

const moduleName = (module: Module) => module.displayName ?? module.name;

/**
 * The chart-level selections shown in the chart's dropdowns, as display names. Used to print the
 * selection into the chart's PNG export, where the dropdowns themselves are hidden.
 */
export const getChartSelectionFields = (
    input: ChartSelectionInput,
): ChartSelectionField[] => {
    const fields = getSelectorFields(input);
    if (input.skillGroupingLabel) {
        fields.push({ label: "Skills", values: [input.skillGroupingLabel] });
    }
    return fields;
};

const getSelectorFields = ({
    selectionType,
    selectedIds,
    modules,
    llmClassifiers,
    hasDataGrid,
}: ChartSelectionInput): ChartSelectionField[] => {
    const fields: ChartSelectionField[] = [];
    const add = (label: string, values: (string | undefined)[]) => {
        const present = values.filter((value): value is string => !!value);
        if (present.length) fields.push({ label, values: present });
    };

    if (
        selectionType === "LLM Classifier" ||
        selectionType === "LLM Classifier Results"
    ) {
        add("Model", [
            llmClassifiers.find((c) => c.id === selectedIds.llmClassifier)
                ?.name,
        ]);
    }

    if (selectionType === "Modules" && !hasDataGrid) {
        add(
            "Modules",
            toIds(selectedIds.modules).map((id) => {
                const module = modules.find((m) => m.id === id);
                return module && moduleName(module);
            }),
        );
    }

    if (
        selectionType !== "Module" &&
        selectionType !== "Question" &&
        selectionType !== "Questions" &&
        selectionType !== "Tags"
    ) {
        return fields;
    }

    const module = modules.find((m) => m.id === selectedIds.module);
    if (!module) return fields;
    add("Module", [moduleName(module)]);

    const findQuestion = (id: string) =>
        module.questions.find((q) => `${q.id}` === id);

    if (selectionType === "Question" || selectionType === "Tags") {
        const [questionId] = toIds(selectedIds.question);
        const question = questionId ? findQuestion(questionId) : undefined;
        add("Question", [question && questionName(question)]);

        if (selectionType === "Tags" && question && !hasDataGrid) {
            const tagIds = toIds(selectedIds.tags);
            add(
                "Answers",
                (question.tags ?? [])
                    .filter((tag) => tagIds.includes(`${tag.id}`))
                    .map((tag) => tag.value),
            );
        }
    }

    if (selectionType === "Questions" && !hasDataGrid) {
        add(
            "Questions",
            toIds(selectedIds.questions).map((id) => {
                const question = findQuestion(id);
                return question && questionName(question);
            }),
        );
    }

    return fields;
};
